import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Box, Button, LinearProgress, Typography } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import GameScreenShell from "@components/Home/Games/GameScreenShell";
import SessionSummary, { SessionSummaryStats } from "@components/Home/Games/SessionSummary";
import type { GameSessionGame } from "@models/game";
import { GAME_IDS } from "@models/game";
import {
  useStartSessionMutation,
  useSubmitRoundMutation,
  useCompleteSessionMutation,
} from "@store/gameApi";
import { RootState } from "@store/store";
import {
  getLevelParams,
  generateProblem,
  type MathProblem,
} from "./mathBlitzContent";
import { gameContinueButtonSx } from "@components/Home/Games/gameUi";
import { useGameTimingMode } from "@hooks/useGameTimingMode";
import { useInGameMoreTimeAllowed } from "@hooks/useInGameMoreTimeAllowed";
import { adjustMathBlitzTiming, getInGameMoreTimeBonusMs } from "@utils/gameTiming";

export interface MathBlitzGuidedProps {
  guidedSessionId?: string;
  guidedCurrentGame?: GameSessionGame;
  guidedGameIndex?: number;
  guidedTotalGames?: number;
  onGuidedGameComplete?: () => void;
}

const ROUNDS_PER_SESSION = 3;
const PROBLEMS_PER_ROUND = 10;
const FEEDBACK_DELAY_MS = 1500;
const SESSION_START_DEDUPE_MS = 2000;

let lastSessionStartAt = 0;

type Phase = "idle" | "playing" | "feedback";

interface ProblemResult {
  question: string;
  userAnswer: number | null;
  correctAnswer: number;
  reactionTime: number | null;
  timedOut: boolean;
}

interface FreePlayLocationState {
  freePlaySessionId?: string;
  freePlayGames?: GameSessionGame[];
}

const MathBlitz: FC<MathBlitzGuidedProps> = ({
  guidedSessionId,
  guidedCurrentGame,
  guidedGameIndex = 0,
  guidedTotalGames = 1,
  onGuidedGameComplete,
}) => {
  const location = useLocation();
  const freePlayState = location.state as FreePlayLocationState | null;
  const freePlaySessionId = freePlayState?.freePlaySessionId;
  const freePlayGame = freePlayState?.freePlayGames?.[0];

  const isGuided = Boolean(guidedSessionId && guidedCurrentGame);
  const isFreePlayFromLibrary = Boolean(freePlaySessionId && freePlayGame);
  const effectiveSessionId = guidedSessionId ?? freePlaySessionId ?? null;
  const effectiveGame = guidedCurrentGame ?? freePlayGame;

  const { t } = useTranslation();
  const navigate = useNavigate();
  const selectedPatientId = useSelector(
    (state: RootState) => state.home.selectedPatientId
  );
  const sessionStartedRef = useRef(false);
  const [startSession] = useStartSessionMutation();
  const [sessionStartError, setSessionStartError] = useState<string | null>(null);
  const [submitRound] = useSubmitRoundMutation();
  const [completeSession] = useCompleteSessionMutation();
  const contentSeed = effectiveGame?.contentSeed ?? "";
  const difficulty = effectiveGame
    ? Math.max(1, Math.min(10, effectiveGame.difficulty))
    : 1;
  const gameTimingMode = useGameTimingMode();
  const inGameMoreTimeAllowed = useInGameMoreTimeAllowed();
  const levelParams = useMemo(
    () => adjustMathBlitzTiming(getLevelParams(difficulty), gameTimingMode),
    [difficulty, gameTimingMode]
  );

  const [sessionId, setSessionId] = useState<string | null>(effectiveSessionId);
  const [phase, setPhase] = useState<Phase>("idle");
  const [roundCount, setRoundCount] = useState(0);
  const [roundProblems, setRoundProblems] = useState<MathProblem[]>([]);
  const [roundStartTime, setRoundStartTime] = useState(0);
  const [problemIndex, setProblemIndex] = useState(0);
  const [problemStartTime, setProblemStartTime] = useState(0);
  const [problemResults, setProblemResults] = useState<ProblemResult[]>([]);
  const [xpEarned, setXpEarned] = useState<number | null>(null);
  const [paused, setPaused] = useState(false);
  const [sessionStartTime, setSessionStartTime] = useState<number | null>(null);
  const [timerSeconds, setTimerSeconds] = useState(0);
  const [sessionScore, setSessionScore] = useState(0);
  const [roundResults, setRoundResults] = useState<
    { accuracy: number; reactionMs: number }[]
  >([]);
  const [domainUpdatesSum, setDomainUpdatesSum] = useState<
    Record<string, number>
  >({});
  const [showSessionSummary, setShowSessionSummary] = useState(false);
  const [summaryStats, setSummaryStats] = useState<SessionSummaryStats | null>(
    null
  );
  const [muted, setMuted] = useState(false);
  const [tapFeedback, setTapFeedback] = useState<
    "none" | "correct" | "wrong"
  >("none");
  const [streak, setStreak] = useState(0);
  const [countdownTick, setCountdownTick] = useState(0);
  const [problemBonusMs, setProblemBonusMs] = useState(0);
  const [moreTimeUsedForProblem, setMoreTimeUsedForProblem] = useState(false);
  const feedbackTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const countdownIntervalRef = useRef<ReturnType<typeof setInterval> | null>(
    null
  );

  const currentProblem =
    roundProblems.length > 0 && problemIndex < roundProblems.length
      ? roundProblems[problemIndex]
      : null;

  const startNewRound = useCallback((roundIndex?: number) => {
    const round = roundIndex ?? roundCount;
    const seed = contentSeed ? `${contentSeed}-${round}` : `local-${round}`;
    const problems: MathProblem[] = [];
    for (let i = 0; i < PROBLEMS_PER_ROUND; i++) {
      problems.push(generateProblem(difficulty, seed, i));
    }
    const now = Date.now();
    setRoundProblems(problems);
    setProblemIndex(0);
    setRoundStartTime(now);
    setProblemStartTime(now);
    setProblemResults([]);
    setTapFeedback("none");
    setXpEarned(null);
    setProblemBonusMs(0);
    setMoreTimeUsedForProblem(false);
    setPhase("playing");
  }, [contentSeed, roundCount, difficulty]);

  useEffect(() => {
    if (isGuided || isFreePlayFromLibrary) return;
    if (!selectedPatientId) {
      setSessionStartError(t("games.errors.onlyPatientCanPlay"));
      return;
    }
    if (sessionStartedRef.current) return;
    const now = Date.now();
    if (now - lastSessionStartAt < SESSION_START_DEDUPE_MS) return;
    lastSessionStartAt = now;
    sessionStartedRef.current = true;
    setSessionStartError(null);
    let mounted = true;
    startSession({ patientId: selectedPatientId })
      .unwrap()
      .then((res) => {
        if (mounted && res?.sessionId) {
          setSessionId(res.sessionId);
          setSessionStartTime(Date.now());
        }
      })
      .catch((err: { status?: number }) => {
        if (mounted && err?.status === 403) {
          setSessionStartError(t("games.errors.onlyPatientCanPlay"));
        }
      });
    return () => {
      mounted = false;
    };
  }, [isGuided, isFreePlayFromLibrary, selectedPatientId, startSession, t]);

  useEffect(() => {
    if ((isGuided && guidedSessionId) || isFreePlayFromLibrary) {
      setSessionStartTime(Date.now());
    }
  }, [isGuided, guidedSessionId, isFreePlayFromLibrary]);

  useEffect(() => {
    if (
      phase !== "playing" ||
      !currentProblem ||
      paused ||
      problemResults.length > problemIndex
    )
      return;
    const id = setInterval(() => setCountdownTick((c) => c + 1), 100);
    return () => clearInterval(id);
  }, [phase, currentProblem, paused, problemIndex, problemResults.length]);

  useEffect(() => {
    if (sessionStartTime == null || paused) return;
    const id = setInterval(() => {
      setTimerSeconds(Math.floor((Date.now() - sessionStartTime) / 1000));
    }, 1000);
    return () => clearInterval(id);
  }, [sessionStartTime, paused]);

  // Tick for countdown bar animation (every 100ms)
  useEffect(() => {
    if (
      phase !== "playing" ||
      !currentProblem ||
      paused ||
      problemResults.length > problemIndex
    )
      return;
    const id = setInterval(() => setCountdownTick((c) => c + 1), 100);
    return () => clearInterval(id);
  }, [phase, currentProblem, paused, problemIndex, problemResults.length]);

  // Per-problem countdown: when time runs out, record timeout and advance after FEEDBACK_DELAY_MS
  useEffect(() => {
    if (
      phase !== "playing" ||
      !currentProblem ||
      paused ||
      problemResults.length > problemIndex
    )
      return;
    const deadline =
      problemStartTime + levelParams.timePerProblemMs + problemBonusMs;
    countdownIntervalRef.current = setInterval(() => {
      const now = Date.now();
      if (now >= deadline) {
        if (countdownIntervalRef.current) {
          clearInterval(countdownIntervalRef.current);
          countdownIntervalRef.current = null;
        }
        setProblemResults((prev) => {
          if (prev.length > problemIndex) return prev;
          return [
            ...prev,
            {
              question: currentProblem.question,
              userAnswer: null,
              correctAnswer: currentProblem.correctAnswer,
              reactionTime: null,
              timedOut: true,
            },
          ];
        });
        setTapFeedback("wrong");
        setStreak(0);
      feedbackTimeoutRef.current = setTimeout(() => {
        setProblemIndex((i) => i + 1);
        setProblemStartTime(Date.now());
        setProblemBonusMs(0);
        setMoreTimeUsedForProblem(false);
        setTapFeedback("none");
      }, FEEDBACK_DELAY_MS);
    }
  }, 100);
    return () => {
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = null;
      }
      // Do not clear feedbackTimeoutRef here: it is set by handleAnswerTap (or by
      // the timeout branch above). When we add a result, problemResults.length
      // changes and this effect re-runs; clearing feedbackTimeoutRef would cancel
      // the advance-to-next-problem timeout and leave the UI stuck on "Next problem...".
    };
  }, [
    phase,
    currentProblem,
    problemStartTime,
    problemIndex,
    problemResults.length,
    levelParams.timePerProblemMs,
    problemBonusMs,
    paused,
  ]);

  // When problemIndex reaches PROBLEMS_PER_ROUND, go to feedback
  useEffect(() => {
    if (phase !== "playing" || problemIndex < PROBLEMS_PER_ROUND) return;
    setPhase("feedback");
  }, [phase, problemIndex]);

  // Clear feedback timeout on unmount so we don't run setState after unmount
  useEffect(() => {
    return () => {
      if (feedbackTimeoutRef.current) {
        clearTimeout(feedbackTimeoutRef.current);
        feedbackTimeoutRef.current = null;
      }
    };
  }, []);

  const handleAnswerTap = useCallback(
    (value: number) => {
      if (phase !== "playing" || !currentProblem || problemResults.length > problemIndex)
        return;
      const reactionTime = Date.now() - problemStartTime;
      const correct = value === currentProblem.correctAnswer;
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = null;
      }
      setProblemResults((prev) => [
        ...prev,
        {
          question: currentProblem.question,
          userAnswer: value,
          correctAnswer: currentProblem.correctAnswer,
          reactionTime,
          timedOut: false,
        },
      ]);
      if (correct) {
        setTapFeedback("correct");
        setStreak((s) => s + 1);
      } else {
        setTapFeedback("wrong");
        setStreak(0);
      }
      feedbackTimeoutRef.current = setTimeout(() => {
        setProblemIndex((i) => i + 1);
        if (problemIndex + 1 < PROBLEMS_PER_ROUND) {
          setProblemStartTime(Date.now());
        }
        setProblemBonusMs(0);
        setMoreTimeUsedForProblem(false);
        setTapFeedback("none");
      }, FEEDBACK_DELAY_MS);
    },
    [phase, currentProblem, problemIndex, problemResults.length, problemStartTime]
  );

  const handleNextRound = useCallback(() => {
    const nextRound = roundCount + 1;
    const correctCount = problemResults.filter(
      (r) => !r.timedOut && r.userAnswer === r.correctAnswer
    ).length;
    const accuracy = PROBLEMS_PER_ROUND > 0 ? correctCount / PROBLEMS_PER_ROUND : 0;
    const reactionTimes = problemResults
      .filter((r) => r.reactionTime != null)
      .map((r) => r.reactionTime!);
    const avgReactionMs =
      reactionTimes.length > 0
        ? reactionTimes.reduce((a, b) => a + b, 0) / reactionTimes.length
        : 0;

    setRoundResults((prev) => [...prev, { accuracy, reactionMs: avgReactionMs }]);

    if (sessionId) {
      submitRound({
        sessionId,
        body: {
          gameId: GAME_IDS.mathBlitz,
          roundNumber: nextRound,
          difficulty,
          accuracy,
          reactionTimeMs:
            reactionTimes.length > 0 ? Math.round(avgReactionMs) : null,
          completionTimeMs: Date.now() - roundStartTime,
          errorCount: PROBLEMS_PER_ROUND - correctCount,
          rawData: {
            problems: problemResults.map((r) => ({
              question: r.question,
              userAnswer: r.userAnswer,
              correctAnswer: r.correctAnswer,
              reactionTime: r.reactionTime,
              timedOut: r.timedOut,
            })),
          },
        },
      })
        .unwrap()
        .then((res) => {
          if (res?.xpEarned != null) {
            setXpEarned(res.xpEarned);
            setSessionScore((s) => s + res.xpEarned);
          }
          if (res?.domainUpdates && Object.keys(res.domainUpdates).length > 0) {
            setDomainUpdatesSum((prev) => {
              const next = { ...prev };
              for (const [k, v] of Object.entries(res.domainUpdates)) {
                next[k] = (next[k] ?? 0) + v;
              }
              return next;
            });
          }
        })
        .catch(() => {});
    } else {
      const xp = accuracy >= 0.8 ? 20 : 10;
      setXpEarned(xp);
      setSessionScore((s) => s + xp);
    }

    setRoundCount(nextRound);

    if (nextRound >= ROUNDS_PER_SESSION) {
      if (isGuided && onGuidedGameComplete) {
        onGuidedGameComplete();
        return;
      }
      const allResults = [...roundResults, { accuracy, reactionMs: avgReactionMs }];
      const totalRounds = allResults.length;
      const avgAccuracy =
        totalRounds > 0
          ? (allResults.reduce((s, r) => s + r.accuracy, 0) / totalRounds) * 100
          : 0;
      const avgReactionSec =
        totalRounds > 0
          ? allResults.reduce((s, r) => s + r.reactionMs, 0) / totalRounds / 1000
          : 0;

      if (sessionId) {
        completeSession({ sessionId })
          .unwrap()
          .then((res) => {
            setSummaryStats({
              accuracyPercent: avgAccuracy,
              avgReactionSeconds: avgReactionSec,
              xpEarned: res?.totalXp ?? sessionScore,
              domainChanges: domainUpdatesSum,
            });
          })
          .catch(() => {
            setSummaryStats({
              accuracyPercent: avgAccuracy,
              avgReactionSeconds: avgReactionSec,
              xpEarned: sessionScore,
              domainChanges: domainUpdatesSum,
            });
          });
      } else {
        setSummaryStats({
          accuracyPercent: avgAccuracy,
          avgReactionSeconds: avgReactionSec,
          xpEarned: sessionScore,
          domainChanges: domainUpdatesSum,
        });
      }
      lastSessionStartAt = 0;
      setSessionId(null);
      setShowSessionSummary(true);
      return;
    }
    startNewRound(nextRound);
  }, [
    roundCount,
    problemResults,
    roundStartTime,
    sessionId,
    isGuided,
    onGuidedGameComplete,
    completeSession,
    submitRound,
    startNewRound,
    roundResults,
    sessionScore,
    domainUpdatesSum,
    difficulty,
  ]);

  const handleExit = useCallback(() => {
    if (sessionId) completeSession({ sessionId }).catch(() => {});
    lastSessionStartAt = 0;
    navigate(-1);
  }, [sessionId, completeSession, navigate]);

  const handleRequestMoreTime = useCallback(() => {
    if (moreTimeUsedForProblem || phase !== "playing") return;
    setProblemBonusMs(
      (ms) => ms + getInGameMoreTimeBonusMs(GAME_IDS.mathBlitz)
    );
    setMoreTimeUsedForProblem(true);
  }, [moreTimeUsedForProblem, phase]);

  const handleSummaryContinue = useCallback(() => navigate(-1), [navigate]);

  const progress =
    phase === "idle"
      ? 0
      : (roundCount + (phase === "feedback" ? 1 : 0)) / ROUNDS_PER_SESSION;

  const countdownProgress =
    currentProblem && phase === "playing" && problemResults.length <= problemIndex
      ? Math.max(
          0,
          1 -
            (Date.now() - problemStartTime) /
              (levelParams.timePerProblemMs + problemBonusMs)
        )
      : 1;

  const feedbackText =
    phase === "feedback"
      ? null
      : tapFeedback === "correct"
        ? t("games.mathBlitz.feedbackCorrect")
        : tapFeedback === "wrong"
          ? t("games.mathBlitz.feedbackWrong")
          : null;

  if (showSessionSummary && summaryStats) {
    return (
      <SessionSummary stats={summaryStats} onContinue={handleSummaryContinue} />
    );
  }

  return (
    <GameScreenShell
      gameName={t("games.mathBlitz.title")}
      level={difficulty}
      progress={progress}
      feedback={feedbackText}
      timerSeconds={sessionStartTime != null ? timerSeconds : null}
      score={sessionScore > 0 ? sessionScore : null}
      paused={paused}
      onBack={handleExit}
      onPause={() => setPaused((p) => !p)}
      gameIndex={isGuided ? guidedGameIndex : undefined}
      totalGames={isGuided ? guidedTotalGames : undefined}
      muted={muted}
      onMuteChange={setMuted}
      moreTime={
        inGameMoreTimeAllowed && phase === "playing"
          ? {
              onRequest: handleRequestMoreTime,
              disabled: moreTimeUsedForProblem,
            }
          : undefined
      }
    >
      {phase === "idle" && (
        <Box
          textAlign="center"
          py={4}
          flex={1}
          display="flex"
          flexDirection="column"
          justifyContent="center"
        >
          {sessionStartError && (
            <Typography color="error" sx={{ mb: 2 }}>
              {sessionStartError}
            </Typography>
          )}
          <Typography variant="h6" gutterBottom>
            {t("games.mathBlitz.instructionsTitle")}
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 3 }}>
            {t("games.mathBlitz.instructions")}
          </Typography>
          <Button
            variant="contained"
            size="large"
            onClick={() => startNewRound()}
            sx={gameContinueButtonSx}
          >
            {t("games.mathBlitz.startRound")}
          </Button>
        </Box>
      )}

      {(phase === "playing" || phase === "feedback") && (
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
            width: "100%",
          }}
        >
          {phase === "playing" && currentProblem && problemResults.length <= problemIndex && (
            <Box
              sx={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                gap: { xs: 3, sm: 4 },
                width: "100%",
                minHeight: { xs: "min(52vh, 480px)", sm: "min(45vh, 440px)" },
                py: { xs: 1, sm: 2 },
              }}
            >
              <Box sx={{ textAlign: "center", flexShrink: 0 }}>
                {streak > 0 && (
                  <Typography variant="body2" color="primary.main" sx={{ mb: 1.5 }}>
                    {t("games.mathBlitz.streak", { count: streak })}
                  </Typography>
                )}
                <Typography
                  variant="h4"
                  fontWeight={700}
                  sx={{ fontSize: "1.75rem", textAlign: "center" }}
                  data-countdown-tick={countdownTick}
                >
                  {currentProblem.question} = ?
                </Typography>
              </Box>

              <Box
                sx={{
                  width: "100%",
                  maxWidth: { xs: "100%", sm: 440 },
                  mx: "auto",
                  flexShrink: 0,
                  px: { xs: 0.5, sm: 0 },
                }}
              >
                <LinearProgress
                  variant="determinate"
                  value={countdownProgress * 100}
                  aria-hidden
                  sx={{
                    height: 10,
                    borderRadius: 1,
                    bgcolor: "action.hover",
                    "& .MuiLinearProgress-bar": {
                      bgcolor: countdownProgress > 0.25 ? "primary.main" : "warning.main",
                    },
                  }}
                />
              </Box>

              <Box
                display="grid"
                gridTemplateColumns="1fr 1fr"
                gap={2.5}
                width="100%"
                maxWidth={{ xs: "100%", sm: 440 }}
                mx="auto"
                alignSelf="stretch"
                sx={{ flexShrink: 0 }}
              >
                {currentProblem.options.map((opt) => (
                  <Button
                    key={opt}
                    variant="contained"
                    size="large"
                    onClick={() => handleAnswerTap(opt)}
                    disabled={paused}
                    sx={{
                      minHeight: { xs: 88, sm: 96 },
                      width: "100%",
                      py: 2,
                      fontSize: { xs: "1.75rem", sm: "2rem" },
                      fontWeight: 700,
                    }}
                  >
                    {opt}
                  </Button>
                ))}
              </Box>
            </Box>
          )}

          {phase === "playing" &&
            problemResults.length > problemIndex &&
            problemIndex < roundProblems.length && (
              <Box textAlign="center" py={2}>
                <Typography variant="body1" color="text.secondary">
                  {t("games.mathBlitz.nextProblem")}
                </Typography>
              </Box>
            )}

          {phase === "feedback" && (
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              gap={2}
              sx={{ flex: 1, py: 2 }}
            >
              {xpEarned != null && (
                <Typography variant="body2" color="primary.main">
                  +{xpEarned} XP
                </Typography>
              )}
              <Button
                variant="contained"
                size="large"
                onClick={handleNextRound}
                sx={gameContinueButtonSx}
              >
                {roundCount + 1 >= ROUNDS_PER_SESSION
                  ? t("games.mathBlitz.finishSession")
                  : t("games.mathBlitz.nextRound")}
              </Button>
            </Box>
          )}
        </Box>
      )}
    </GameScreenShell>
  );
};

export default MathBlitz;
